import { notification } from 'antd'
import { ArgsProps, NotificationConfig } from 'antd/es/notification/interface'
import { createContext, FC } from 'react'

interface NotificationProviderProps {
  children: React.ReactNode
}

export interface NotifyType {
  success: (_: ArgsProps) => void
  warning: (_: ArgsProps) => void
  error: (_: ArgsProps) => void
  info: (_: ArgsProps) => void
  destroy: (_: string) => void
}

export enum NOTIFY_KEYS {
  ONLINE_OFFLINE = 'ONLINE_OFFLINE',
  AUTHORIZATION_SOURCE_CHANGED = 'AUTHORIZATION_SOURCE_CHANGED',
  ASSESSMENT_VALIDATION_ERROR = 'ASSESSMENT_VALIDATION_ERROR',
  ASSESSMENT_FINAL_ROUND_QA = 'ASSESSMENT_FINAL_ROUND_QA',
  SCHEDULE_CONFLICTS = 'SCHEDULE_CONFLICTS',
  PAT_GENERATION_ERROR = 'PAT_GENERATION_ERROR',
  SKILLED_NEEDS_REGENERATED = 'SKILLED_NEEDS_REGENERATED',
  RESOURCE_GENERATION_NOTIFICATION = 'RESOURCE_GENERATION_NOTIFICATION',
  ORDER_CREATION_NOTIFICATION = 'ORDER_CREATION_NOTIFICATION',
  ORDER_RESULT_NOTIFICATION = 'ORDER_RESULT_NOTIFICATION',
  DOCUMENTS_VALIDATION_ERROR = 'DOCUMENTS_VALIDATION_ERROR',
  SUPERVISORY_VALIDATION_ERROR = 'SUPERVISORY_VALIDATION_ERROR',
  DOWNLOAD_CSV_REPORT_ERROR = 'DOWNLOAD_CSV_REPORT_ERROR',
  AUTHORIZATION_SOURCE_MIXED_TYPES_ERROR = 'AUTHORIZATION_SOURCE_MIXED_TYPES_ERROR',
  AUTHORIZATION_SOURCE_DUPLICATE_SERVICE_TYPES_ERROR = 'AUTHORIZATION_SOURCE_DUPLICATE_SERVICE_TYPES_ERROR',
  AUTHORIZATION_SOURCE_EXPIRED = 'AUTHORIZATION_SOURCE_EXPIRED',
  UPDATE_USER_ACCESS_SUCCESS = 'UPDATE_USER_ACCESS_SUCCESS',
  UPDATE_USER_ACCESS_ERROR = 'UPDATE_USER_ACCESS_ERROR',
  AUTHORIZATION_NON_ACTIVE = 'AUTHORIZATION_NON_ACTIVE',
  DOC_DROP_UPLOAD = 'DOC_DROP_UPLOAD',
  /** Shared keyed toast for in-viewer page mutations (reorder, delete, …). */
  DOC_PAGE_EDITOR = 'DOC_PAGE_EDITOR',
  PATIENT_SOR_LINK = 'PATIENT_SOR_LINK',
  CAREGIVER_SOR_LINK = 'CAREGIVER_SOR_LINK',
  DOCUMENT_ORDER_RECEIVED_INFO_MISSING = 'DOCUMENT_ORDER_RECEIVED_INFO_MISSING'
}

export const NotificationContext = createContext({
  notify: {
    success: (_: ArgsProps) => {},
    warning: (_: ArgsProps) => {},
    error: (_: ArgsProps) => {},
    info: (_: ArgsProps) => {},
    destroy: (_: string) => {}
  } as NotifyType
})

export const NotificationProvider: FC<NotificationProviderProps> = ({ children }) => {
  const notificationConfig: NotificationConfig = {}

  const messageConfig: Partial<ArgsProps> = {
    placement: 'bottomRight',
    duration: 5
  }

  const NOTIFY_ALLOWED_KEYS = [
    NOTIFY_KEYS.ONLINE_OFFLINE,
    NOTIFY_KEYS.ASSESSMENT_VALIDATION_ERROR,
    NOTIFY_KEYS.ASSESSMENT_FINAL_ROUND_QA,
    NOTIFY_KEYS.SCHEDULE_CONFLICTS,
    NOTIFY_KEYS.PAT_GENERATION_ERROR,
    NOTIFY_KEYS.RESOURCE_GENERATION_NOTIFICATION,
    NOTIFY_KEYS.ORDER_RESULT_NOTIFICATION,
    NOTIFY_KEYS.DOCUMENTS_VALIDATION_ERROR,
    NOTIFY_KEYS.SUPERVISORY_VALIDATION_ERROR,
    NOTIFY_KEYS.UPDATE_USER_ACCESS_SUCCESS,
    NOTIFY_KEYS.UPDATE_USER_ACCESS_ERROR,
    NOTIFY_KEYS.AUTHORIZATION_NON_ACTIVE,
    NOTIFY_KEYS.AUTHORIZATION_SOURCE_EXPIRED,
    NOTIFY_KEYS.DOC_DROP_UPLOAD,
    NOTIFY_KEYS.DOC_PAGE_EDITOR,
    NOTIFY_KEYS.PATIENT_SOR_LINK,
    NOTIFY_KEYS.CAREGIVER_SOR_LINK,
    NOTIFY_KEYS.DOCUMENT_ORDER_RECEIVED_INFO_MISSING
  ]

  const [api, contextHolder] = notification.useNotification(notificationConfig)

  /** Accepts an allowed key or a namespaced key such as `DOC_DROP_UPLOAD.<documentRequestId>`. */
  const isAllowedKey = (key: string): boolean =>
    NOTIFY_ALLOWED_KEYS.includes(key.split('.')[0] as NOTIFY_KEYS)

  const hydrateMessage = (message: ArgsProps): ArgsProps => {
    if (message.key && !isAllowedKey(message.key.toString())) {
      // eslint-disable-next-line no-console
      console.warn(
        `Notification key '${message.key}' not defined in allowed notification keys. Add key to NOTIFY_ALLOWED_KEYS`
      )
    }

    return {
      ...messageConfig,
      ...message
    }
  }

  const success = (message: ArgsProps): void => {
    api.success(hydrateMessage(message))
  }

  const error = (message: ArgsProps): void => {
    api.error(hydrateMessage(message))
  }

  const warning = (message: ArgsProps): void => {
    api.warning(hydrateMessage(message))
  }

  const info = (message: ArgsProps): void => {
    api.info(hydrateMessage(message))
  }

  const destroy = (key: string): void => {
    if (!isAllowedKey(key)) {
      // eslint-disable-next-line no-console
      console.warn(
        `Notification key '${key}' not defined in allowed notification keys. It's possible the notification you're trying to dismiss does not exist. Add key to NOTIFY_ALLOWED_KEYS`
      )
    }

    api.destroy(key)
  }

  return (
    <NotificationContext.Provider
      value={{
        notify: {
          success,
          error,
          warning,
          info,
          destroy
        }
      }}
    >
      {contextHolder}
      {children}
    </NotificationContext.Provider>
  )
}
